import { useEffect } from 'react';
import { LedgerStatus } from '@shared/types/entities/ledger';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import { useConnectionStore } from '@ewf/stores/connection';
import { useBalanceStore } from '@ewf/stores/balance';
import { formatLedgerError, useLedgerStore } from '@ewf/stores/ledger';
import { useApiServiceStore } from '@ewf/stores/api';
import { SunkenBox } from '@ewf/components/SunkenBox';
import {
  ProgressBadgeEWX,
  ProgressBadgeEthereum,
  ProgressBadgePolkadot,
  ProgressBadgeUSB,
  ProgressBadgeVerify,
} from '@ewf/components/StatusBadge/ProgressBadge';
import { CloseButton } from '@ewf/components/ModalUI/CloseButton';
import Web3 from 'web3';
import { DEVICE_TYPE } from '@shared/types/enums';
import { useWalletEnvStore } from '@ewf/stores/wallet-env';

export const ConnectLedger = ({
  chain,
  onClose,
  onConnectionSuccess,
  onConnectionError,
}: Props) => {
  const workerApi = useApiServiceStore.getState().api;
  const isConnected = useConnectionStore((state) => state.isConnected);
  const client = useConnectionStore((state) => state.client);
  const onSessionConnected = useConnectionStore((state) => state.onSessionConnected);
  const setBalanceEWC = useBalanceStore((state) => state.setBalanceEWC);
  const status = useLedgerStore((state) => state.status);

  // On component mount, open the WalletConnect QR code panel to connect the wallet.
  // Once the process is done, go to the next step.
  useEffect(() => {
    const isEWC = chain === MARKETPLACE_CHAINS.EWC;

    const openLedger = async () => {
      try {
        const plugged = await workerApi.isDevicePlugged();
        if (!plugged) {
          onConnectionError('Please plug in or unlock your Ledger device');
          return;
        }

        const locked = await workerApi.isDeviceLocked();
        if (locked) {
          onConnectionError('Please unlock ledger device');
          return;
        }

        const { address, balance, path, errorStatus } = await workerApi.deviceConnect(
          DEVICE_TYPE.Ledger,
          chain,
        );

        if (errorStatus) {
          const errorMsg = formatLedgerError(errorStatus);
          onConnectionError(errorMsg);
          return;
        }

        if (isEWC) {
          useConnectionStore.setState({ addressEWC: address });
          setBalanceEWC({
            token: balance,
            base: Web3.utils.toWei(balance, 'ether'),
            symbol: 'EWT',
          });
        } else if (chain === MARKETPLACE_CHAINS.ETH) {
          useConnectionStore.setState({ addressETH: address });

          // For ETH, we need both EWT token balance and native ETH gas balance
          const { walletConst } = useWalletEnvStore.getState();

          if (!walletConst.ethRpc) {
            throw new Error('No Ethereum RPC found');
          }

          const httpProvider = new Web3.providers.HttpProvider(walletConst.ethRpc);
          const web3 = new Web3(httpProvider);

          // Get native ETH balance for gas fees
          const ethBalance = await web3.eth.getBalance(address);

          useBalanceStore.getState().setBalanceETH({
            token: balance, // EWT token balance from Ledger
            base: Web3.utils.toWei(balance, 'ether'),
            symbol: 'EWT',
            gas: {
              token: Web3.utils.fromWei(ethBalance, 'ether'),
              base: ethBalance,
              symbol: 'ETH',
            },
          });

          httpProvider.disconnect();
        } else {
          // EWX chain
          await onSessionConnected({ type: 'ledger', address, balance, path });
        }

        onConnectionSuccess();
      } catch (error) {
        console.error(error);

        const errorMsg =
          error instanceof Error
            ? error.message
            : error?.toString
              ? error.toString()
              : 'An unknown error occurred';
        onConnectionError(errorMsg);
      }
    };

    openLedger();
  }, []);

  const stepComponents: Partial<Record<LedgerStatus, React.ReactNode>> = {
    'open-application': (() => {
      const isEthereumBased = chain === MARKETPLACE_CHAINS.EWC || chain === MARKETPLACE_CHAINS.ETH;
      const appName = isEthereumBased ? 'Ethereum' : 'Polkadot';
      const badge = isEthereumBased ? <ProgressBadgeEthereum /> : <ProgressBadgePolkadot />;

      return (
        <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
          <SunkenBox className="mb-4 ml-auto text-center">
            <p className="text-lg font-bold">Open {appName} App on your Ledger Device</p>
          </SunkenBox>
          <SunkenBox className="flex justify-center p-8">{badge}</SunkenBox>
        </div>
      );
    })(),
    connecting: (
      <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
        <SunkenBox className="mb-4 ml-auto text-center">
          <p className="text-lg font-bold">Connecting to Ledger Device</p>
        </SunkenBox>
        <SunkenBox className="flex justify-center p-8">
          <ProgressBadgeUSB />
        </SunkenBox>
      </div>
    ),
    'verify-address': (
      <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
        <SunkenBox className="mb-4 ml-auto text-center">
          <p className="text-lg font-bold">Verify Address on Ledger Device</p>
        </SunkenBox>
        <SunkenBox className="flex justify-center p-8">
          <ProgressBadgeVerify />
        </SunkenBox>
      </div>
    ),
    'accept-transaction': (
      <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
        <SunkenBox className="mb-4 ml-auto text-center">
          <p className="text-lg font-bold">Accept Transaction</p>
        </SunkenBox>
        <SunkenBox className="flex justify-center p-8">
          <ProgressBadgeVerify />
        </SunkenBox>
      </div>
    ),
    'executing-transaction': (
      <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
        <SunkenBox className="mb-4 ml-auto text-center">
          <p className="text-lg font-bold">Executing Transaction...</p>
        </SunkenBox>
        <SunkenBox className="flex justify-center p-8">
          <ProgressBadgeEWX />
        </SunkenBox>
      </div>
    ),
  };

  return (
    <div className="w-[550px] rounded-lg border-2 border-transparent p-8 gradient-border-primary-with-gray-90">
      <section className="flex w-full justify-end">
        <CloseButton onClick={onClose} />
      </section>
      {stepComponents[status] || (
        <div className="mt-8 rounded-lg p-6 bg-popup-gradient">
          <SunkenBox className="mb-4 ml-auto text-center">
            <p className="text-lg font-bold">Please Continue in Ledger device</p>
          </SunkenBox>
          <SunkenBox className="flex justify-center p-8">
            <ProgressBadgeUSB />
          </SunkenBox>
        </div>
      )}
    </div>
  );
};

interface Props {
  chain: MARKETPLACE_CHAINS;
  onClose?: () => void;
  onConnectionSuccess: () => void;
  onConnectionError: (msg?: string) => void;
}
